import React, { useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Wallet, Zap } from 'lucide-react';
import { Panel, Empty } from './Primitives';
import { useFetch } from '../lib/hooks';
import { api, errMsg, fmtDate } from '../lib/api';

const inp = 'h-11 px-3 rounded-lg bg-vj-charcoal border border-border text-sm text-foreground focus:border-vj-neon/60 focus:outline-none';

export function DepositWallets({ onChange }) {
  const { data: wallets, reload } = useFetch('/deposit-wallets');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!/^0x[a-fA-F0-9]{40}$/.test(address.trim())) return toast.error('Enter a valid 0x… BEP20 wallet address');
    setBusy(true);
    try {
      await api.post('/deposit-wallets', { address: address.trim(), label: label.trim() || 'My wallet' });
      toast.success('Wallet linked — deposits from it will be credited automatically');
      setAddress(''); setLabel(''); reload(); onChange?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/deposit-wallets/${id}`); reload(); toast.success('Wallet removed'); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Panel title="My Sending Wallets" testId="deposit-wallets-panel"
      action={<span className="text-[11px] text-vj-neon inline-flex items-center gap-1"><Zap size={12} /> Auto-detect</span>}>
      <p className="text-xs text-vj-grey mb-4">Link the BEP20 wallet(s) you send USDT from. Every transfer from a linked wallet to the VEYJEX address is detected on-chain and attributed to you automatically — no transaction hash needed.</p>
      <form onSubmit={add} className="grid sm:grid-cols-[1fr_160px_auto] gap-2 mb-4">
        <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="0x… your wallet address" className={`${inp} font-mono`} data-testid="deposit-wallet-address" />
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional)" className={inp} data-testid="deposit-wallet-label" />
        <button disabled={busy} className="h-11 px-5 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm disabled:opacity-60" data-testid="deposit-wallet-add">Link wallet</button>
      </form>
      {wallets && wallets.length ? (
        <div className="space-y-2">
          {wallets.map((w) => (
            <div key={w.id} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0" data-testid={`deposit-wallet-${w.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Wallet size={16} className="text-vj-gold shrink-0" />
                <div className="min-w-0"><div className="text-sm">{w.label}</div><div className="font-mono text-xs text-vj-grey truncate">{w.address}</div></div>
              </div>
              <span className="text-[11px] text-vj-grey hidden sm:block">{fmtDate(w.created_at)}</span>
              <button onClick={() => remove(w.id)} className="text-vj-grey hover:text-vj-red" data-testid={`deposit-wallet-remove-${w.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      ) : <Empty>No wallets linked yet.</Empty>}
    </Panel>
  );
}
